import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import { Mail, RefreshCw, X } from 'lucide-react';
import TableCustom from '../../../../../components/v2/TableCustom';
import EmptyCreatePage from '../../../../../components/v2/EmptyCreatePage';
import { Badge } from '../../../../../empreenderUI/ui/Badge/Badge';
import { Button } from '../../../../../empreenderUI/ui/Button';
import { Checkbox } from '../../../../../empreenderUI/ui/Checkbox';
import { translations } from '../../../../../utils/language';
import { MOCK_ORDERS_STATE, USE_MOCK_ORDERS } from '../../../mocks/ordersMock';
import FiltersOrders from '../filters-orders';
import ModalMenuSinc from '../../modal-menu-sinc';
import ModalSearchPlatform from '../../modal-search-platform';
import ModalSearchAllPlatformDefault from '../../modal-search-all-platform-default';
import ModalFeedResendEmail from '../modal-feed-resend-email';
import ModalFeedCompleted from '../../../../../components/tailwind/modal-feed-completed';
import ModalMenuItem from '../../modal-menu-item';
import ModalEditEmail from '../../modal-edit-email';
import ModalLicenca from '../../modal-licenca';
import ModalEmails from '../../modal-emails';
import OrderProductsGroup from '../../order-products-group';

function getLoginData() {
  try {
    const raw = localStorage.getItem('depoimentos@login');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function TableOrders(props) {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();

  const [page, setpage] = useState(1);
  const [perPage] = useState(40);
  const [search, setSearch] = useState('');
  const [dateInicioFilter, setDateInicioFilter] = useState('');
  const [dateFimFilter, setDateFimFilter] = useState('');

  const [dataOrders, setDataOrders] = useState([]);
  const language = useSelector((state) => state.reducerLanguage);
  const lang = language?.data || 'pt';
  const ordersFromStore = useSelector((state) => state.reducerSales);
  const orders = USE_MOCK_ORDERS ? MOCK_ORDERS_STATE : ordersFromStore;

  const hasSak = getLoginData()?.integrationSak != null;

  const [visibleModalMenuSinc, setVisibleModalMenuSinc] = useState(false);
  const [visibleModalCompletedFeed, setVisibleModalCompletedFeed] = useState(false);
  const [visibleModalSearchPlatform, setVisibleModalSearchPlatform] = useState(false);
  const [visibleModalSearchAllPlatform, setVisibleModalSearchAllPlatform] = useState(false);
  const [visibleModalFeedResendEmail, setVisibleModalFeedResendEmail] = useState(false);
  const [dataModalResendEmailCompleted, setDataModalResendEmailCompleted] = useState([]);
  const [productLoadingId, setProductLoadingId] = useState(null);

  const [menuItem, setMenuItem] = useState(null);
  const [pendingMenuOpen, setPendingMenuOpen] = useState(false);
  const [visibleModalMenu, setVisibleModalMenu] = useState(false);
  const [visibleModalEdit, setVisibleModalEdit] = useState(false);
  const [visibleModalLicenca, setVisibleModalLicenca] = useState(false);
  const [visibleModalEmails, setVisibleModalEmails] = useState(false);

  useEffect(() => {
    if (orders?.data?.data?.length > 0) setDataOrders(orders.data.data);
    else if (orders?.data?.data == undefined) setDataOrders([]);
  }, [orders]);

  const allSelected = dataOrders.length > 0 && selectedOrders.length === dataOrders.length;
  const someSelected = selectedOrders.length > 0 && selectedOrders.length < dataOrders.length;

  const handleClick = () => {
    setSelectedOrders([]);
  };

  const toggleAll = (value) => {
    setSelectedOrders(value === true ? dataOrders : []);
  };

  const isSelected = (item) => selectedOrders.some((o) => o.id === item.id);

  const toggleSelect = (item, value) => {
    setSelectedOrders((prev) => (value === true ? [...prev, item] : prev.filter((p) => p.id !== item.id)));
  };

  const removeQueryParam = (param) => {
    const url = new URL(window.location);
    url.searchParams.delete(param);
    window.history.pushState({}, '', url);
  };

  const goSearch = () => {
    if (USE_MOCK_ORDERS) {
      removeQueryParam('pageOrder');
      return;
    }
    dispatch({
      type: 'SAGA_LIST_SALES',
      payload: {
        search: search.replace('#', ''),
        perPage,
        page: location.search.length > 0 ? location.search.split('=')[1] : page,
        iniDate: dateInicioFilter,
        finDate: dateFimFilter,
      },
    });
    removeQueryParam('pageOrder');
  };

  useEffect(() => {
    goSearch();
  }, [page, dateInicioFilter, dateFimFilter]);

  const requestItems = async () => {
    for (const item of selectedOrders) {
      try {
        setProductLoadingId(item.id);
        const token = getLoginData()?.access_token?.token;
        const url = `${process.env.REACT_APP_API_URL}/re-send_email`;
        const headers = new Headers({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });
        await fetch(url, { method: 'POST', headers, body: JSON.stringify([item]) });
        setDataModalResendEmailCompleted((prevData) => [...prevData, item.id]);
        setProductLoadingId(null);
      } catch (error) {
        console.error(`Erro na requisição do item ${item.name}:`, error);
      }
    }
    setVisibleModalFeedResendEmail(false);
    setVisibleModalCompletedFeed(true);
  };

  const closeModal = () => {
    setVisibleModalCompletedFeed(false);
    setTimeout(() => {
      handleClick();
    }, 10);
    setDataModalResendEmailCompleted([]);
  };

  const openRowMenu = (item) => {
    // Mount ModalMenuItem first with visible=false, then open after commit.
    // react-modal often fails when the component mounts already with isOpen=true.
    setMenuItem(item);
    setVisibleModalMenu(false);
    setVisibleModalEdit(false);
    setVisibleModalLicenca(false);
    setVisibleModalEmails(false);
    setPendingMenuOpen(true);
  };

  useEffect(() => {
    if (!menuItem || !pendingMenuOpen) return;
    setVisibleModalMenu(true);
    setPendingMenuOpen(false);
  }, [menuItem, pendingMenuOpen]);

  const columns = useMemo(() => {
    const cols = [
      {
        label: (
          <Checkbox
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            onCheckedChange={toggleAll}
            aria-label={translations['common-select-all-aria'][lang]}
          />
        ),
        className: 'w-10',
      },
      { label: translations['sales-pedido'][lang], className: 'w-36' },
      { label: translations['sales-th-nome-email'][lang], className: 'w-auto' },
      { label: translations['sales-th-produtos-chaves'][lang], className: 'w-40' },
      { label: translations['Status'][lang], className: 'w-28' },

      { label: translations['sales-th-envio-email'][lang], className: 'w-28' },
    ];

    if (hasSak) {
      cols.push({ label: translations['sales-th-envio-whatsapp'][lang], className: 'w-28' });
    }

    cols.push({ label: '', className: 'w-12' });
    return cols;
  }, [lang, hasSak, allSelected, someSelected, dataOrders]);

  const rows = useMemo(
    () =>
      dataOrders.map((item) => {
        const orderLabel = `#${item.order_number != null ? item.order_number : item.order_id}`;
        const isCanceled = item.is_canceled == 1;
        const emailSent = item.is_email_send == 1;

        const orderDate = item.created_at ? new Date(item.created_at).toLocaleString() : '—';

        const cells = [
          <Checkbox
            key='check'
            checked={isSelected(item)}
            onCheckedChange={(value) => toggleSelect(item, value)}
            aria-label={`${translations['products-select-aria'][lang]} ${translations['sales-pedido'][lang].toLowerCase()} ${orderLabel}`}
          />,
          <div key='order' className='flex flex-col gap-0.5'>
            {item.order_items != null && item.order_link ? (
              <button
                type='button'
                className='text-sm font-medium text-blue-600 hover:text-blue-700 text-left'
                onClick={() => {
                  window.open(item.order_link, '_blank');
                }}
              >
                {orderLabel}
              </button>
            ) : (
              <span className='text-sm text-gray-900'>{orderLabel}</span>
            )}
            <span className='text-xs text-gray-500 whitespace-nowrap'>{orderDate}</span>
          </div>,
          <div key='name' className='flex flex-col gap-0.5 min-w-0'>
            <p className='truncate text-sm text-gray-900' title={item.customer_name}>
              {item.customer_name || '—'}
            </p>
            <p className='truncate text-xs text-gray-500' title={item.customer_email}>
              {item.customer_email || '—'}
            </p>
          </div>,
          <OrderProductsGroup
            key='products'
            id={item.id}
            orderItems={item.order_items}
            orderItemsCodes={item.order_items_codes}
          />,

          <Badge key='status' variant='primary' color={isCanceled ? 'red' : 'green'}>
            {translations[isCanceled ? 'sales-status-bloqueado' : 'sales-status-liberado'][lang]}
          </Badge>,
          <Badge key='email-status' variant='primary' color={emailSent ? 'yellow' : 'green'}>
            {translations['sales-status-enviado'][lang]}
          </Badge>,
        ];

        if (hasSak) {
          cells.push(
            <Badge key='whats-status' variant='primary' color={emailSent ? 'yellow' : 'green'}>
              {translations['sales-status-enviado'][lang]}
            </Badge>,
          );
        }

        cells.push(
          <Button key='menu' type='button' variant='secondary' size='icon' className='size-8' onClick={() => openRowMenu(item)}>
            <EllipsisVerticalIcon className='size-5' />
          </Button>,
        );

        return cells;
      }),
    [dataOrders, selectedOrders, hasSak, lang],
  );

  const pagination = orders?.data?.meta;

  const filters = (
    <FiltersOrders
      dateInicioFilter={dateInicioFilter}
      setDateInicioFilter={setDateInicioFilter}
      dateFimFilter={dateFimFilter}
      setDateFimFilter={setDateFimFilter}
    />
  );

  return (
    <>
      <ModalFeedResendEmail
        setVisible={setVisibleModalFeedResendEmail}
        visible={visibleModalFeedResendEmail}
        selectedOrders={selectedOrders}
        dataModalSincCompleted={dataModalResendEmailCompleted}
        setDataModalSincCompleted={setDataModalResendEmailCompleted}
        productLoadingId={productLoadingId}
        goSearch={props.goSearch}
        handleClick={handleClick}
      />

      <ModalFeedCompleted
        setVisible={setVisibleModalCompletedFeed}
        visible={visibleModalCompletedFeed}
        title={translations['sales-reenvio-completo'][lang]}
        desc={translations['sales-reenvio-completo-desc'][lang]}
        close={closeModal}
      />

      <ModalMenuSinc
        goSearch={goSearch}
        visibleModalSearchPlatform={visibleModalSearchPlatform}
        setVisibleModalSearchPlatform={setVisibleModalSearchPlatform}
        visibleModalSearchAllPlatform={visibleModalSearchAllPlatform}
        setVisibleModalSearchAllPlatform={setVisibleModalSearchAllPlatform}
        visible={visibleModalMenuSinc}
        setVisibleModal={setVisibleModalMenuSinc}
      />
      <ModalSearchPlatform goSearch={goSearch} visible={visibleModalSearchPlatform} setVisibleModal={setVisibleModalSearchPlatform} />
      <ModalSearchAllPlatformDefault goSearch={goSearch} visible={visibleModalSearchAllPlatform} setVisibleModal={setVisibleModalSearchAllPlatform} />

      {menuItem && (
        <>
          <ModalLicenca visible={visibleModalLicenca} setVisibleModal={setVisibleModalLicenca} item={menuItem} />
          <ModalEmails visible={visibleModalEmails} setVisibleModal={setVisibleModalEmails} item={menuItem} />
          <ModalMenuItem
            visibleModalEmails={visibleModalEmails}
            setVisibleModalEmails={setVisibleModalEmails}
            visibleModalLicenca={visibleModalLicenca}
            setVisibleModalLicenca={setVisibleModalLicenca}
            setVisibleModalEdit={setVisibleModalEdit}
            item={menuItem}
            visible={visibleModalMenu}
            setVisibleModal={setVisibleModalMenu}
          />
          <ModalEditEmail id={menuItem.id} visible={visibleModalEdit} setVisibleModal={setVisibleModalEdit} />
        </>
      )}

      {!orders.loading && dataOrders.length === 0 ? (
        <EmptyCreatePage
          showAlert={false}
          showButtons={false}
          title={translations['page-orders-title'][lang]}
          description={translations['sales-nao-pedidos'][lang]}
          benefits={[
            translations['sales-empty-benefit-1'][lang],
            translations['sales-empty-benefit-2'][lang],
            translations['sales-empty-benefit-3'][lang],
            translations['sales-empty-benefit-4'][lang],
            translations['sales-empty-benefit-5'][lang],
          ]}
        />
      ) : (
        <div className='px-4 sm:px-6 lg:px-8 w-full max-w-full min-w-0 box-border'>
          <div className='flex items-start justify-between gap-4 mb-6 min-w-0'>
            <div className='min-w-0'>
              <h1 className='text-2xl font-semibold tracking-tight text-gray-900'>{translations['page-orders-title'][lang]}</h1>
              <p className='mt-2 text-sm text-gray-500'>{translations['page-orders-description'][lang]}</p>
            </div>
          </div>

          <TableCustom
            className='w-full max-w-full min-w-0'
            title={null}
            setSearch={setSearch}
            searchValue={search}
            searchPlaceholder={translations['sales-pesquisar-pedido'][lang]}
            onSearchSubmit={() => {
              setpage(1);
              goSearch();
            }}
            columns={columns}
            data={dataOrders}
            rows={rows}
            pagination={pagination}
            isLoading={!!orders.loading}
            getData={(p) => setpage(p)}
            filters={filters}
            actionTable={[
              {
                label: (
                  <>
                    <RefreshCw className='size-4' />
                    {translations['sales-filtro-sincronizar'][lang]}
                  </>
                ),
                onClick: () => setVisibleModalMenuSinc(true),
                variant: 'outline',
                visible: true,
              },
            ]}
            getRowKey={(item) => item?.id}
          />
        </div>
      )}

      {selectedOrders.length > 0 && (
        <div className='fixed bottom-4 left-1/2 z-50 w-[min(920px,calc(100%-2rem))] -translate-x-1/2 rounded-xl border border-slate-700/80 bg-slate-900/95 px-4 sm:px-5 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.45)] backdrop-blur-sm'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='min-w-0'>
              <p className='text-sm font-semibold text-white'>
                {selectedOrders.length} {selectedOrders.length === 1 ? translations['sales-selected-one'][lang] : translations['sales-selected-many'][lang]}
              </p>
              <p className='text-xs text-slate-400'>{translations['sales-reenviar-email'][lang]}</p>
            </div>
            <div className='flex items-center gap-2 shrink-0'>
              <Button
                variant='outline'
                className='border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white hover:border-slate-500'
                onClick={() => {
                  requestItems();
                  setVisibleModalFeedResendEmail(true);
                }}
              >
                <Mail className='size-4' />
                {translations['sales-reenviar-email'][lang]}
              </Button>
              <Button
                type='button'
                variant='secondary'
                size='icon'
                className='size-8 shrink-0 border-0 bg-slate-700 text-slate-200 hover:bg-slate-600 hover:text-white'
                aria-label={translations['common-clear-selection'][lang]}
                onClick={() => setSelectedOrders([])}
              >
                <X className='size-4' />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
